import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { contact } from "../data/content";
import { TinyStar, CircleScribble, ArrowSticker } from "../components/Stickers";
import { toast } from "sonner";

export default function Contact() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("צריך למלא את כל השדות");
      return;
    }
    setSending(true);
    // Open mailto with prefilled content
    const subject = encodeURIComponent(`פניה מהאתר · ${form.name}`);
    const body = encodeURIComponent(
      `שלום,\n\n${form.message}\n\n— ${form.name}\n${form.email}`,
    );
    window.location.href = `mailto:${contact.email}?subject=${subject}&body=${body}`;
    setTimeout(() => {
      setSending(false);
      toast.success("נפתח לך חלון מייל. נדבר בקרוב.");
    }, 600);
  };

  return (
    <main
      data-testid="contact-page"
      className="relative mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 pt-16 md:pt-24 pb-16"
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="tag-pill">יצירת קשר · Say Hi</span>
      </div>

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="font-display font-black tracking-tight leading-[0.88] text-[15vw] md:text-[10vw]"
      >
        {contact.big}
        <span className="text-[color:var(--pink)]">.</span>
      </motion.h1>

      <p className="mt-8 max-w-2xl text-xl md:text-2xl text-[color:var(--ink)] leading-relaxed">
        {contact.sub}
      </p>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        {/* Contact info */}
        <div className="md:col-span-5 space-y-8">
          <div>
            <p className="text-sm tracking-widest uppercase text-[color:var(--charcoal)] mb-2">
              מייל
            </p>
            <a
              href={`mailto:${contact.email}`}
              data-testid="contact-email"
              className="font-display font-bold text-2xl md:text-3xl border-b-2 border-[color:var(--ink)] hover:text-[color:var(--pink)] hover:border-[color:var(--pink)] transition-colors"
            >
              {contact.email}
            </a>
          </div>

          <div>
            <p className="text-sm tracking-widest uppercase text-[color:var(--charcoal)] mb-3">
              גם שם
            </p>
            <ul className="flex flex-wrap gap-3">
              {contact.social.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contact-social-${s.label.toLowerCase()}`}
                    className="tag-pill hover:bg-[color:var(--pink)] hover:text-[color:var(--paper)] hover:border-[color:var(--pink)] transition-colors"
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center gap-3 pt-4 text-[color:var(--charcoal)]">
            <TinyStar className="w-4 h-4" color="#FF1493" />
            <p className="font-serif-editorial italic text-lg">
              עונה תוך 24-48 שעות בימי חול.
            </p>
          </div>

          <CircleScribble
            className="w-32 h-32 -mt-2 -mr-2 animate-spin-slow opacity-90"
            color="#FF1493"
          />
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="md:col-span-7 space-y-5"
        >
          <Field
            label="איך קוראים לך?"
            type="text"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            testId="contact-name"
            placeholder="השם המלא"
          />
          <Field
            label="מייל לחזרה"
            type="email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            testId="contact-email-input"
            placeholder="you@example.com"
          />
          <Field
            label="מה הסיפור?"
            type="textarea"
            value={form.message}
            onChange={(v) => setForm({ ...form, message: v })}
            testId="contact-message"
            placeholder="פרויקט? שאלה? רעיון מטורף?"
          />

          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="inline-flex items-center gap-3 bg-[color:var(--ink)] text-[color:var(--paper)] px-7 py-4 rounded-full font-display font-bold text-lg hover:bg-[color:var(--pink)] transition-colors disabled:opacity-50 group"
          >
            {sending ? "שולחת..." : "שלחי"}
            <ArrowSticker
              className="w-7 h-5 rotate-180 group-hover:translate-x-[-4px] transition-transform"
              color="#FDFCF8"
            />
          </button>
        </form>
      </div>
    </main>
  );
}

function Field({ label, type, value, onChange, testId, placeholder }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[color:var(--charcoal)] mb-2">
        {label}
      </span>
      {type === "textarea" ? (
        <textarea
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={6}
          className="w-full bg-transparent border-2 border-[color:var(--ink)] rounded-2xl px-4 py-3 text-lg focus:outline-none focus:border-[color:var(--pink)] transition-colors resize-none"
        />
      ) : (
        <input
          data-testid={testId}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-transparent border-2 border-[color:var(--ink)] rounded-2xl px-4 py-3 text-lg focus:outline-none focus:border-[color:var(--pink)] transition-colors"
        />
      )}
    </label>
  );
}
